import { Drawer } from 'antd';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import ReactDOM from 'react-dom';
import closeIcon from '../../assets/svg/close-icon.svg';
import getFormatMessage from '../../locales/get-format-message';
import { CancelButton, CommonButtonGroup, SubmitButton } from '../button/main';
import styles from './index.module.scss';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '../../store';

interface IProps {
  title?: string;
  className?: string;
  children: React.ReactNode;
  onClose?: () => void;
  onOk?: () => Promise<boolean>;
  footer?: ReactNode;
  okText?: string;
  cancelText?: string;
  okButton?: ReactNode;
  okButtonDisabled?: boolean;
  hideOkButton?: boolean;
  width?: number;
  hideFooter?: boolean;
  loading?: boolean;
}

export const ConsoleDrawer = (props: IProps) => {
  const {
    onClose,
    onOk,
    okText,
    cancelText,
    okButton,
    hideOkButton = false,
    width,
    okButtonDisabled = false,
    hideFooter = false,
    loading = false,
  } = props;
  const [submitting, setSubmitting] = useState<boolean>(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onOk?.();
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
    }
  };

  const getFooter = () => {
    if (props.footer) {
      return props.footer;
    }
    if (hideFooter) {
      return null;
    }
    return (
      <CommonButtonGroup>
        <CancelButton onClick={onClose}>
          {cancelText || getFormatMessage('cancel')}
        </CancelButton>
        {!hideOkButton &&
          (okButton || (
            <SubmitButton
              onClick={submit}
              loading={loading || submitting}
              disabled={okButtonDisabled}
            >
              {okText || getFormatMessage('save')}
            </SubmitButton>
          ))}
      </CommonButtonGroup>
    );
  };
  return (
    <Drawer
      className={classNames(props.className, styles.consoleDrawer)}
      closable={false}
      onClose={props.onClose}
      visible={true}
      headerStyle={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        marginLeft: '3px',
      }}
      width={width || 720}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{props.title}</span>
          <img
            src={closeIcon}
            className="sync-close-icon"
            onClick={props.onClose}
          />
        </div>
      }
      footer={getFooter()}
    >
      {props.children}
    </Drawer>
  );
};

ConsoleDrawer.show = (props: IProps) => {
  const id = `common-drawer-${Math.random()}`;
  const container = document.createElement('div');
  container.id = id;
  document.body.appendChild(container);

  const destroy = () => {
    ReactDOM.render(<></>, container);
    document.body.removeChild(container);
  };

  const onClose = async () => {
    await props.onClose?.();
    destroy();
  };

  const onOk = async () => {
    try {
      const res = await props.onOk?.();
      if ((props.onOk && res === true) || !props.onOk) {
        destroy();
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const drawerProps: IProps = {
    ...props,
    onOk,
    onClose,
  };

  ReactDOM.render(
    <Provider store={store}>
      <BrowserRouter>
        <ConsoleDrawer {...drawerProps} />
      </BrowserRouter>
    </Provider>,
    container
  );

  return { destroy };
};
